import React, {useEffect, useState} from "react";
import {getManualPayments} from "../../../services/admin_service/manual_payment";
import {getPersonRole} from "../../../services/user/login";
import {ManualPaymentForm} from "./ManualPaymentForm";
import {ManualPaymentReview} from "./ManualPaymentReview";
import {currentPaymentRole, MANUAL_PAYMENT_STATUSES} from "./manualPaymentModel";

import {BsArrowClockwise, BsChevronLeft, BsChevronRight, BsCreditCard2Front, BsInbox, BsPlusLg} from "react-icons/bs";
import {ManualPaymentTable} from "./ManualPaymentTable";
import "./manual-payment.css";

const allowedRoles = ["admin", "support", "back_office"];
const emptyResult = {items: [], total: 0, page: 1, limit: 20};
const buttonClass = "manual-payment-button";
const statusFilters = [{value: "", label: "همه"}, ...Object.entries(MANUAL_PAYMENT_STATUSES).map(([value, {label}]) => ({value, label}))];

export function ManualPaymentManagement({onChanged}) {
  const [role, setRole] = useState("");
  const [roleLoading, setRoleLoading] = useState(true);
  const [roleError, setRoleError] = useState("");
  const [roleRevision, setRoleRevision] = useState(0);
  const [query, setQuery] = useState({page: 1, limit: 20, status: "pending"});
  const [result, setResult] = useState(emptyResult);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [reviewId, setReviewId] = useState(null);

  useEffect(() => {
    let active = true;
    setRoleLoading(true);
    setRoleError("");
    getPersonRole().then(response => {
      if (!active) return;
      if (response?.status !== 200) throw new Error("دریافت نقش فعال کاربر انجام نشد.");
      setRole(currentPaymentRole(response.data));
    }).catch(() => { if (active) setRoleError("دریافت نقش فعال کاربر انجام نشد."); })
      .finally(() => { if (active) setRoleLoading(false); });
    return () => { active = false; };
  }, [roleRevision]);

  useEffect(() => {
    if (!allowedRoles.includes(role)) return undefined;
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");
    getManualPayments({...query, signal: controller.signal}).then(next => {
      if (!active) return;
      const lastPage = Math.max(1, Math.ceil(next.total / next.limit));
      if (next.page > lastPage) {
        setQuery(current => ({...current, page: lastPage, limit: next.limit}));
        return;
      }
      setResult({...next, items: next.items || []});
    }).catch(cause => {
      if (active && cause?.code !== "ERR_CANCELED") setError(cause.message || "دریافت واریزی‌ها انجام نشد.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [role, query, revision]);

  const changed = () => {
    setRevision(value => value + 1);
    onChanged?.();
  };
  const totalPages = Math.max(1, Math.ceil(result.total / result.limit));
  const from = result.total === 0 ? 0 : (result.page - 1) * result.limit + 1;
  const to = Math.min(result.page * result.limit, result.total);

  if (roleLoading) return <p role="status" className="manual-payment-state">در حال دریافت دسترسی واریزی‌ها…</p>;
  if (roleError) return <div role="alert" className="manual-payment-error">{roleError} <button type="button" onClick={() => setRoleRevision(value => value + 1)} className="underline">تلاش دوباره</button></div>;
  if (!allowedRoles.includes(role)) return null;

  return (
    <section dir="rtl" aria-labelledby="manual-payment-heading" className="manual-payments">
      <header className="manual-payments-header">
        <div className="manual-payments-title">
          <span className="manual-payment-icon"><BsCreditCard2Front aria-hidden="true"/></span>
          <div><h2 id="manual-payment-heading">واریزی‌های کارت به کارت</h2><p>{role === "admin" ? "بررسی واریزی‌های ثبت‌شده توسط کارشناسان" : "واریزی‌های ثبت‌شده توسط شما و نتیجه بررسی ادمین"}</p></div>
        </div>
        <button type="button" onClick={() => setFormOpen(true)} className={`${buttonClass} is-primary`}><BsPlusLg aria-hidden="true"/>ثبت واریزی کارت به کارت</button>
      </header>
      <div className="manual-payments-toolbar">
        <div role="group" aria-label="وضعیت واریزی" className="manual-payment-status-filters">
          {statusFilters.map(({value, label}) => <button
            key={value} type="button" aria-pressed={query.status === value}
            onClick={() => { if (query.status !== value) setQuery(current => ({...current, status: value, page: 1})); }}
            className="manual-payment-status-filter"
          >{value && <span aria-hidden="true" className={`manual-payment-filter-dot is-${value}`}/>} {label}</button>)}
        </div>
        {!loading && !error && <span className="manual-payment-count">{result.total.toLocaleString("fa-IR")} واریزی {query.status ? "در این وضعیت" : "در مجموع"}</span>}
        <button type="button" onClick={() => setRevision(value => value + 1)} disabled={loading} className={`${buttonClass} manual-payment-refresh`}><BsArrowClockwise aria-hidden="true"/>تازه‌سازی واریزی‌ها</button>
      </div>
      {loading ? <div role="status" className="manual-payment-state"><span className="manual-payment-spinner" aria-hidden="true"/>در حال دریافت واریزی‌ها…</div> : error ? <div role="alert" className="manual-payment-error">{error} <button type="button" className="underline" onClick={() => setRevision(value => value + 1)}>تلاش دوباره</button></div> : <>
        {result.items.length > 0 ? <ManualPaymentTable items={result.items} canReview={role === "admin"} onReview={setReviewId}/> : <div className="manual-payment-state">
          <span className="manual-payment-icon"><BsInbox aria-hidden="true"/></span>
          <strong>واریزی‌ای با این وضعیت پیدا نشد.</strong><p>برای مشاهده موارد دیگر، وضعیت واریزی را تغییر دهید.</p>
        </div>}
        <footer className="manual-payments-footer">
          <span>نمایش {from.toLocaleString("fa-IR")} تا {to.toLocaleString("fa-IR")} از {result.total.toLocaleString("fa-IR")} واریزی</span>
          <label className="manual-payment-page-size">تعداد واریزی در هر صفحه <select value={result.limit} onChange={event => setQuery(current => ({...current, page: 1, limit: Number(event.target.value)}))}>{[...new Set([10,20,50,100,result.limit])].sort((a,b)=>a-b).map(limit => <option key={limit} value={limit}>{limit.toLocaleString("fa-IR")}</option>)}</select></label>
          <nav className="manual-payment-pagination" aria-label="صفحه‌بندی واریزی‌ها">
            <button type="button" aria-label="صفحه قبل واریزی‌ها" disabled={result.page <= 1} onClick={() => setQuery(current => ({...current, page: result.page - 1, limit: result.limit}))} className={buttonClass}><BsChevronRight aria-hidden="true"/></button>
            <span>{result.page.toLocaleString("fa-IR")} از {totalPages.toLocaleString("fa-IR")}</span>
            <button type="button" aria-label="صفحه بعد واریزی‌ها" disabled={result.page >= totalPages} onClick={() => setQuery(current => ({...current, page: result.page + 1, limit: result.limit}))} className={buttonClass}><BsChevronLeft aria-hidden="true"/></button>
          </nav>
        </footer>
      </>}
      <ManualPaymentForm open={formOpen} onClose={() => setFormOpen(false)} onCreated={() => {setFormOpen(false); changed();}}/>
      {reviewId !== null && role === "admin" && <ManualPaymentReview key={reviewId} paymentId={reviewId} onClose={() => setReviewId(null)} onChanged={changed}/>}
    </section>
  );
}
